import { Input, Textarea, Checkbox, Select, SelectItem } from "@nextui-org/react";
import React from "react";

export interface FormComponentRenderProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  value?: any;
  onChange?: (e: any) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  options?: Array<{ label: string, value: string }>;
  [key: string]: any;
}

export interface ValidationField {
  type: string; // text, number, checkbox, etc.
  label: string;
  placeholder?: string;
  description?: string;
  options?: Array<{ label: string, value: string }>;
}

export interface FormComponentDefinition {
  label: string;
  icon: React.ReactNode;
  component: React.FC<FormComponentRenderProps>;
  config: Record<string, any>;
  validate: (value: any, config: any) => string | undefined;
  validationFields?: ValidationField[]; // Custom validation fields specific to this component type
}

export const formComponentRegistry: Record<string, FormComponentDefinition> = {
  TextInput: {
    label: "Text Input",
    icon: "📝",
    component: (props: FormComponentRenderProps) => {
      // If the value is long (over 100 chars), use a Textarea for scrollability
      const isLongContent = props.value && typeof props.value === 'string' && props.value.length > 100;
      const { isReadOnlyMode, readOnly, ...otherProps } = props;

      if (isLongContent) {
        return (
          <Textarea
            {...otherProps}
            minRows={2}
            maxRows={6}
            classNames={{
              inputWrapper: "border-1 border-black bg-content1",
              input: "bg-content1"
            }}
            style={{
              overflow: 'auto',
              resize: 'vertical',
              ...props.style
            }}
            readOnly={readOnly || isReadOnlyMode}
            onKeyDown={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
            onInput={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
            onPaste={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
          />
        );
      }

      return (
        <Input
          type="text"
          {...otherProps}
          classNames={{
            inputWrapper: "border-1 border-black bg-content1",
            input: "bg-content1"
          }}
          readOnly={readOnly || isReadOnlyMode}
          onKeyDown={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
          onInput={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
          onPaste={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
        />
      );
    },
    config: { type: "text", placeholder: "Enter text...", required: false },
    validate: (value: any, config: any) =>
      config.required && !value ? "Required" : undefined,
    validationFields: [
      {
        type: "text",
        label: "Min Length",
        placeholder: "e.g. 5",
        description: "Minimum number of characters"
      },
      {
        type: "text",
        label: "Max Length",
        placeholder: "e.g. 100",
        description: "Maximum number of characters"
      },
      {
        type: "text",
        label: "Pattern (regex)",
        placeholder: "e.g. ^[a-zA-Z0-9]+$",
        description: "Regular expression for validation"
      }
    ]
  },
  RichTextInput: {
    label: "Rich Text Input",
    icon: "📄",
    component: (props: FormComponentRenderProps) => {
      const { isReadOnlyMode, readOnly, ...otherProps } = props;
      return (
        <Textarea
          {...otherProps}
          minRows={3}
          maxRows={8}
          classNames={{
            inputWrapper: "border-1 border-black bg-content1",
            input: "bg-content1"
          }}
          style={{
            overflow: 'auto',
            resize: 'vertical',
            ...props.style
          }}
          readOnly={readOnly || isReadOnlyMode}
          onKeyDown={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
          onInput={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
          onPaste={readOnly || isReadOnlyMode ? (e) => e.preventDefault() : undefined}
        />
      );
    },
    config: { placeholder: "Enter rich text...", required: false },
    validate: (value: any, config: any) =>
      config.required && !value ? "Required" : undefined,
    validationFields: [
      {
        type: "text",
        label: "Min Length",
        placeholder: "e.g. 10",
        description: "Minimum number of characters"
      },
      {
        type: "text",
        label: "Max Length",
        placeholder: "e.g. 500",
        description: "Maximum number of characters"
      }
    ]
  },
  EmailInput: {
    label: "Email Input",
    icon: "✉️",
    component: (props: FormComponentRenderProps) => <Input type="email" {...props} />,
    config: { type: "email", placeholder: "Enter email...", required: false },
    validate: (value: any, config: any) => {
      if (config.required && !value) return "Required";
      if (value && !/^[^@]+@[^@]+\.[^@]+$/.test(value)) return "Invalid email";
      if (value && config.allowedDomains) {
        const domain = value.split('@')[1];
        if (!config.allowedDomains.includes(domain)) {
          return `Email must be from: ${config.allowedDomains.join(', ')}`;
        }
      }
      return undefined;
    },
    validationFields: [
      {
        type: "text",
        label: "Allowed Domains",
        placeholder: "e.g. gmail.com, outlook.com",
        description: "Comma-separated list of allowed email domains"
      }
    ]
  },
  DateInput: {
    label: "Date Input",
    icon: "📅",
    component: (props: FormComponentRenderProps) => <Input type="date" {...props} />,
    config: { type: "date", required: false },
    validate: (value: any, config: any) => {
      if (config.required && !value) return "Required";
      if (value && config.minDate && new Date(value) < new Date(config.minDate)) {
        return `Date must be after ${config.minDate}`;
      }
      if (value && config.maxDate && new Date(value) > new Date(config.maxDate)) {
        return `Date must be before ${config.maxDate}`;
      }
      return undefined;
    },
    validationFields: [
      {
        type: "date",
        label: "Min Date",
        description: "Earliest allowed date"
      },
      {
        type: "date",
        label: "Max Date",
        description: "Latest allowed date"
      }
    ]
  },
  URLInput: {
    label: "URL Input",
    icon: "🔗",
    component: (props: FormComponentRenderProps) => <Input type="url" {...props} />,
    config: { type: "url", placeholder: "Enter URL...", required: false },
    validate: (value: any, config: any) => {
      if (config.required && !value) return "Required";
      if (value && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/.test(value)) {
        return "Invalid URL";
      }
      return undefined;
    },
    validationFields: [
      {
        type: "checkbox",
        label: "Require HTTPS",
        description: "URL must begin with https://"
      }
    ]
  },
  PhoneInput: {
    label: "Phone Input",
    icon: "📞",
    component: (props: FormComponentRenderProps) => <Input type="tel" {...props} />,
    config: { type: "tel", placeholder: "Enter phone number...", required: false },
    validate: (value: any, config: any) => {
      if (config.required && !value) return "Required";
      if (value && config.format) {
        try {
          const regex = new RegExp(config.format);
          if (!regex.test(value)) {
            return config.formatError || "Invalid phone number format";
          }
        } catch (err) {
          console.error("Invalid phone format regex:", config.format);
        }
      }
      return undefined;
    },
    validationFields: [
      {
        type: "text",
        label: "Format (regex)",
        placeholder: "e.g. ^\\d{3}-\\d{3}-\\d{4}$",
        description: "Phone format pattern (e.g., 555-555-5555)"
      },
      {
        type: "text",
        label: "Format Error Message",
        placeholder: "e.g. Please enter phone as XXX-XXX-XXXX",
        description: "Custom error message for format validation"
      }
    ]
  },
  CheckboxInput: {
    label: "Checkbox Input",
    icon: "✓",
    component: (props: FormComponentRenderProps) => (
      <Checkbox isSelected={props.value} onValueChange={props.onChange}>
        {props.label}
      </Checkbox>
    ),
    config: { label: "Checkbox", required: false },
    validate: (value: any, config: any) =>
      config.required && !value ? "Required" : undefined,
    validationFields: []
  },
  NumberInput: {
    label: "Number Input",
    icon: "#",
    component: (props: FormComponentRenderProps) => <Input type="number" {...props} />,
    config: { type: "number", placeholder: "Enter a number...", required: false },
    validate: (value: any, config: any) => {
      if (config.required && (value === undefined || value === null || value === "")) {
        return "Required";
      }
      if (value !== undefined && value !== null && value !== "") {
        const numValue = Number(value);
        if (config.min !== undefined && numValue < config.min) {
          return `Value must be at least ${config.min}`;
        }
        if (config.max !== undefined && numValue > config.max) {
          return `Value must be at most ${config.max}`;
        }
      }
      return undefined;
    },
    validationFields: [
      {
        type: "number",
        label: "Min Value",
        placeholder: "e.g. 0",
        description: "Minimum allowed value"
      },
      {
        type: "number",
        label: "Max Value",
        placeholder: "e.g. 100",
        description: "Maximum allowed value"
      }
    ]
  },
  SelectInput: {
    label: "Select Input",
    icon: "▼",
    component: (props: FormComponentRenderProps) => (
      <Select
        {...props}
        items={props.options || []}
        placeholder={props.placeholder}
        label={props.label}
      >
        {(item: { label: string, value: string }) => <SelectItem key={item.value}>{item.label}</SelectItem>}
      </Select>
    ),
    config: {
      placeholder: "Select an option...",
      required: false,
      options: [
        { label: "Option 1", value: "option1" },
        { label: "Option 2", value: "option2" },
        { label: "Option 3", value: "option3" },
      ]
    },
    validate: (value: any, config: any) =>
      config.required && !value ? "Required" : undefined,
    validationFields: []
  },
  MultiSelectInput: {
    label: "Multi-select Input",
    icon: "▼+",
    component: (props: FormComponentRenderProps) => (
      <Select
        {...props}
        items={props.options || []}
        placeholder={props.placeholder}
        label={props.label}
        selectionMode="multiple"
      >
        {(item: { label: string, value: string }) => <SelectItem key={item.value}>{item.label}</SelectItem>}
      </Select>
    ),
    config: {
      placeholder: "Select options...",
      required: false,
      options: [
        { label: "Option 1", value: "option1" },
        { label: "Option 2", value: "option2" },
        { label: "Option 3", value: "option3" },
      ]
    },
    validate: (value: any, config: any) => {
      if (config.required && (!value || value.length === 0)) {
        return "Required";
      }
      if (config.minSelect && value && value.length < config.minSelect) {
        return `Select at least ${config.minSelect} options`;
      }
      if (config.maxSelect && value && value.length > config.maxSelect) {
        return `Select at most ${config.maxSelect} options`;
      }
      return undefined;
    },
    validationFields: [
      {
        type: "number",
        label: "Min Selections",
        placeholder: "e.g. 1",
        description: "Minimum number of options to select"
      },
      {
        type: "number",
        label: "Max Selections",
        placeholder: "e.g. 3",
        description: "Maximum number of options to select"
      }
    ]
  },
  FileInput: {
    label: "File Input",
    icon: "📎",
    component: (props: FormComponentRenderProps) => <Input type="file" {...props} />,
    config: { type: "file", required: false },
    validate: (value: any, config: any) => {
      if (config.required && (!value || !value.length)) {
        return "Required";
      }
      if (value && value[0] && config.acceptedTypes) {
        const file = value[0];
        const fileType = file.type;
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        const acceptedList = config.acceptedTypes.split(',').map((type: string) => type.trim().toLowerCase());
        const isAccepted = acceptedList.some((accepted: string) => {
          if (accepted.startsWith('.')) {
            // Check extension
            return `.${fileExtension}` === accepted;
          } else {
            // Check MIME type or MIME type category
            return fileType === accepted || fileType.startsWith(`${accepted}/`);
          }
        });

        if (!isAccepted) {
          return config.typeErrorMessage || `Only ${config.acceptedTypes} files are allowed`;
        }
      }

      if (value && value[0] && config.maxSize) {
        const file = value[0];
        const maxSizeBytes = Number(config.maxSize) * 1024 * 1024; // Convert from MB to bytes
        if (file.size > maxSizeBytes) {
          return `File size must be less than ${config.maxSize}MB`;
        }
      }

      return undefined;
    },
    validationFields: [
      {
        type: "text",
        label: "Accepted Types",
        placeholder: "e.g. .pdf,.jpg,.png",
        description: "Comma-separated list of file extensions or MIME types"
      },
      {
        type: "text",
        label: "Type Error Message",
        placeholder: "e.g. Only PDF and image files are allowed",
        description: "Custom error message for file type validation"
      },
      {
        type: "number",
        label: "Max Size (MB)",
        placeholder: "e.g. 5",
        description: "Maximum file size in megabytes"
      }
    ]
  },
};

export type FormComponentType = keyof typeof formComponentRegistry;

export { formComponentRegistry as default }; 